/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

// libraries
import React, { useEffect } from 'react';
import {
    object, func, bool, shape, string, array,
} from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { useHistory, withRouter } from 'react-router';
import { bindActionCreators } from 'redux';
import { useQuery } from '@apollo/client';
import mbpLogger from 'mbp-logger';
import { compose } from 'recompose';

// redux, hooks, helpers
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getOrderContainsSubscriptionItem, getOrderId } from '../../../../state/ducks/Common/Common-Selectors';
import { getProfileInfo, getIsAuthenticatedStatus } from '../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { triggerInterstitialLogin } from '../../../../state/ducks/Member/ducks/Auth/Auth-Actions';
import { getWrapUpProductRefTitle } from '../../../../state/ducks/App/App-Selectors';

import findWrapUpsAndContent from '../../../gql/queries/findWrapUpsAndContent';
import { GRAPHQL_ENV } from '../../../gql';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

// components
import WrapUpPageBuilder from './WrapUpPageBuilder';
import { getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import useSalesforcePersonalizedContentQuery from '../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import SiteLoader from '../../../pages/Checkout/containers/Common/SiteLoader/SiteLoader';
import useClickStreamCustomEventsTracking from '../../../helpers/Personalization/useClickstreamCustomEventsTracking';

const checkWrapUpItemHasDetails = (content) => {
    const itemHasFields = content.filter((data) => {
        if (data.sku && data.sku.name && data.sku.image && data.sku.prices) {
            return true;
        }
        return false;
    });
    if (itemHasFields.length > 0) {
        return true;
    }
    return false;
};

const WrapUpContentContainer = ({
    brand, track, page, isABFallback, orderId, orderContainsSubscriptionItem, isCtaPositionFlag, forceAddOnWrapPlacementVariant,
    actionTriggerInterstitialLogin, isAuthenticated, userProfile, ffIntersterstialEnabled, ffNativeIntersterstialEnabled, addonWrapUpRedesignFlag, forceAddonWrapUpRedesignFlag,
}) => {
    const { data: atcLoginData } = useSalesforcePersonalizedContentQuery({
        queryName: 'atcLoginRedirect',
        interactionName: `${brand?.domain} - Get Campaign - ATC login redirect`,
        featureFlag: ffIntersterstialEnabled,
    });

    const atcLoginRedirect = atcLoginData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const atcLoginEnabled = atcLoginRedirect?.toLowerCase?.() !== 'test';

    const { loading: ctaPositionLoading, variables: ctaPositionVariable, data: isCTAPositionData } = useSalesforcePersonalizedContentQuery({
        queryName: 'addonWrapStickyCTA',
        interactionName: `${brand?.domain} - Get Campaign - Addon Wrapup CTA Position`,
        featureFlag: isCtaPositionFlag && !forceAddOnWrapPlacementVariant,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });

    // CONTROL OR VARIANT(TEST) VALUE for CTA position Sales Force Test
    const isCTAPositionRedesignUserGroup = isCTAPositionData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const ctaPositionVariant = forceAddOnWrapPlacementVariant || isCTAPositionRedesignUserGroup?.toLowerCase() === 'test';

    const { loading: wrapUpRedesignLoading, variables, data: wrapUpRedesignData } = useSalesforcePersonalizedContentQuery({
        queryName: 'addonWrapUpRedesign',
        interactionName: `${brand?.domain} - Get Campaign - Addon and Wrapup Redesign`,
        featureFlag: addonWrapUpRedesignFlag && !forceAddonWrapUpRedesignFlag,
        optInHoldoutParticipation: false,
        resolveIdentity: false,
    });

    useClickStreamCustomEventsTracking({
        salesforceResponse: wrapUpRedesignData?.contentSF,
        isFireImpression: true,
        page: 'wrap-up',
    });

    useClickStreamCustomEventsTracking({
        salesforceResponse: isCTAPositionData?.contentSF,
        isFireImpression: true,
        page: 'wrap-up',
    });

    useEffect(() => {
        if (typeof isCTAPositionRedesignUserGroup !== 'undefined' && !window.wuCTAPositionEventFired && isCTAPositionRedesignUserGroup) {
            // GA tracking
            const eventParam = {
                eventCategory: 'Experiment',
                eventAction: 'Sticky-CTAs',
                eventLabel: ctaPositionVariant ? 'Variant' : 'Control',
                feature_detail: ctaPositionVariant ? 'Variant' : 'Control',
                feature_element: 'Sticky-CTAs',
                feature_category: 'Experiment',
            };
            track(eventParam);
            window.wuCTAPositionEventFired = true;
        }
        return () => {
            window.wuCTAPositionEventFired = false;
        };
    }, [isCTAPositionRedesignUserGroup]);

    // CONTROL OR VARIANT(TEST) VALUE for PDP Redesign Sales Force Test
    const isAddOnRedesign = wrapUpRedesignData?.contentSF?.campaign?.campaignResponses?.[0]?.payload?.userGroup;
    const wrapupAddonRedesign = isAddOnRedesign?.toLowerCase() === 'test'  || forceAddonWrapUpRedesignFlag;

    if (isABFallback) {
        track({
            eventCategory: 'Personalized wrapup',
            eventAction: 'Impression',
            eventLabel: 'Variant/Fallback',
        });
    }

    const history = useHistory();

    const redirectToShipping = () => {
        const isRememberedUser = !!userProfile.email;
        // GA tracking
        const eventParam = {
            eventCategory: 'Experiment',
            eventAction: 'ATC Login Redirect',
            eventLabel: atcLoginEnabled ? 'Control' : 'Variant',
            feature_detail: atcLoginEnabled ? 'Control' : 'Variant',
            feature_element: 'ATC Login Redirect',
            feature_category: 'Experiment',
            experiment_id: atcLoginData?.contentSF?.campaign?.campaignResponses?.[0]?.campaignId,
        };
        if (ffIntersterstialEnabled && !isAuthenticated && !isRememberedUser && orderContainsSubscriptionItem.length <= 0) {
            track(eventParam);
        }
        if (window.wuATCLoginEventFired) {
            window.wuATCLoginEventFired = false;
        }
        if (orderContainsSubscriptionItem.length > 0 && !isAuthenticated) {
            actionTriggerInterstitialLogin(history, true);
        } else if (((ffIntersterstialEnabled && atcLoginEnabled) || ffNativeIntersterstialEnabled) && !isAuthenticated && !isRememberedUser) {
            actionTriggerInterstitialLogin(history);
        } else {
            history.push(`/checkout/shipping/${orderId}`);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const pageData = { ...page, type: 'wrapup' }; // ga pageview

    const fireTrack = () => {
        track({
            eventCategory: 'Test Impression',
            eventAction: 'Wrap up Page',
            nonInteraction: '1',
        });
    };

    // Get wrap up product refrence title from redux and passing it to findWrapUpsAndContent function
    const wrapUpRefTitle = useSelector(getWrapUpProductRefTitle);
    const WRAP_UPS_AND_CONTENT_QUERY = findWrapUpsAndContent(brand, wrapUpRefTitle);
    const { loading, error, data } = useQuery(WRAP_UPS_AND_CONTENT_QUERY);

    if (loading) {
        return null;
    }
    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: WRAP_UPS_AND_CONTENT_QUERY,
            jsError: error,
            component: 'WrapUpContentContainer.js',
            message: `Failed to fetch content data for: content type wrap_up_products on brand ${brand}`,
            env: GRAPHQL_ENV,
        });
        redirectToShipping();
        return null;
    }
    if (!data) {
        mbpLogger.logWarning({
            appName: process.env.npm_package_name,
            query: WRAP_UPS_AND_CONTENT_QUERY,
            component: 'WrapUpContentContainer.js',
            message: 'No data returned for query',
            env: GRAPHQL_ENV,
        });
        redirectToShipping();
        return null;
    }

    // pim data
    const content = data?.findWrapUpsAndContent?.wrapUps || [];

    let hasDetails = false;
    if (checkWrapUpItemHasDetails(content)) {
        hasDetails = true;
    }

    // cms data
    const wrapUpData = {
        findContent: data?.findWrapUpsAndContent,
    };

    if (content.length === 0) {
        redirectToShipping();
        return null;
    }
    if (wrapUpRedesignLoading || variables?.skip || ctaPositionLoading || ctaPositionVariable?.skip) {
        return <SiteLoader />;
    }
    return (
        <WrapUpPageBuilder
            content={content}
            hasDetails={hasDetails}
            wrapUpData={wrapUpData}
            trackWrapUp={fireTrack}
            atcLoginEnabled={atcLoginEnabled}
            wrapupAddonRedesign={wrapupAddonRedesign}
            ctaPositionVariant={ctaPositionVariant}
        />
    );
};
WrapUpContentContainer.propTypes = {
    brand: object.isRequired,
    track: func.isRequired,
    page: object.isRequired,
    isABFallback: bool,
    orderId: string.isRequired,
    orderContainsSubscriptionItem: array,
    actionTriggerInterstitialLogin: func.isRequired,
    ffIntersterstialEnabled: bool.isRequired,
    ffNativeIntersterstialEnabled: bool.isRequired,
    isAuthenticated: bool,
    userProfile: shape({
        email: string,
    }),
    addonWrapUpRedesignFlag: bool.isRequired,
    isCtaPositionFlag: bool.isRequired,
    forceAddonWrapUpRedesignFlag: bool.isRequired,
    forceAddOnWrapPlacementVariant: bool.isRequired,
};

WrapUpContentContainer.defaultProps = {
    isABFallback: false,
    isAuthenticated: false,
    userProfile: {
        email: '',
    },
    orderContainsSubscriptionItem: [],
};

const mapDispatchToProps = (dispatch) => ({
    track: bindActionCreators(trackEvent, dispatch),
    actionTriggerInterstitialLogin: bindActionCreators(triggerInterstitialLogin, dispatch),
});

const mapStateToProps = (state) => ({
    brand: getBrand(state),
    orderId: getOrderId(state),
    isAuthenticated: getIsAuthenticatedStatus(state),
    ffIntersterstialEnabled: getFeatureFlag('is-interstitial-login-enabled')(state),
    ffNativeIntersterstialEnabled: getFeatureFlag('is-native-interstitial-page-enabled')(state),
    orderContainsSubscriptionItem: getOrderContainsSubscriptionItem(state),
    userProfile: getProfileInfo(state),
    addonWrapUpRedesignFlag: getFeatureFlag('is-add-on-and-wrap-up-redesign-enabled')(state),
    isCtaPositionFlag: getFeatureFlag('is-add-on-wrap-up-cta-position-ab-test-enabled')(state),
    forceAddOnWrapPlacementVariant: getFeatureFlag('force-add-on-wrap-up-button-placement-variant')(state),
    forceAddonWrapUpRedesignFlag: getFeatureFlag('is-force-add-on-and-wrap-up-redesign-enabled')(state),
});

const enhance = compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(WrapUpContentContainer);
